import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ShoppingBag, Ban } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [order, setOrder] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelMessage, setCancelMessage] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts >= 6) {
        if (!cancelled) setStatus("failed");
        return;
      }
      attempts++;
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        setOrder(res.data.order);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          return;
        }
        if (res.data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-3xl mx-auto px-5 py-20 text-center">
      {status === "checking" && (
        <div data-testid="status-checking">
          <Loader2 className="mx-auto mb-4 animate-spin text-magenta" size={48} />
          <h1 className="font-display text-3xl mb-2">Vérification du paiement…</h1>
          <p className="text-white/50">Merci de patienter quelques instants.</p>
        </div>
      )}

      {status === "paid" && (
        <div data-testid="status-paid" className="reveal">
          <CheckCircle2 className="mx-auto mb-4 text-magenta" size={64} />
          <h1 className="font-display text-4xl md:text-5xl mb-3 neon-soft">
            Merci pour ta commande&nbsp;!
          </h1>
          {order?.invoice_number && (
            <p className="text-white/40 mb-2 text-sm tracking-widest uppercase">
              N° de facture : <span className="text-magenta">{order.invoice_number}</span>
            </p>
          )}
          <p className="text-white/60 max-w-xl mx-auto mb-8">
            Le paiement a bien été validé. Tu recevras un reçu Stripe par email.
            Ta commande sera préparée et expédiée à l&apos;adresse renseignée.
          </p>
          {order && (
            <div className="fluo-card p-6 text-left max-w-md mx-auto mb-8">
              <div className="text-xs uppercase text-white/40 tracking-widest mb-2">
                Récapitulatif
              </div>
              <ul className="text-sm space-y-1">
                {order.items?.map((i) => (
                  <li key={`${i.product_id}-${i.size}`} className="flex justify-between">
                    <span>
                      {i.product_name}{" "}
                      <span className="text-white/40">
                        ({i.size}) × {i.quantity}
                      </span>
                    </span>
                    <span>{i.subtotal.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-white/10 mt-3 pt-3 text-sm space-y-1">
                <div className="flex justify-between text-white/60">
                  <span>Frais d&apos;envoi</span>
                  <span>{order.shipping_cost?.toFixed(2)} CHF</span>
                </div>
                <div className="flex justify-between font-display text-lg">
                  <span>Total</span>
                  <span className="text-magenta">{order.total?.toFixed(2)} CHF</span>
                </div>
              </div>
            </div>
          )}

          {/* Cancellation block */}
          {order && order.status !== "cancelled" && order.fulfillment_status !== "shipped" && (
            <div className="mb-8 text-sm">
              {cancelMessage ? (
                <div className="fluo-card p-4 max-w-md mx-auto text-white/80 border-magenta/50" data-testid="cancel-message">
                  {cancelMessage}
                </div>
              ) : confirmCancel ? (
                <div className="fluo-card p-5 max-w-md mx-auto border-magenta/60">
                  <p className="text-white/80 mb-3 text-sm">
                    Es-tu sûr ? Le remboursement (si paiement déjà effectué)
                    sera traité sous 1-3 jours ouvrés.
                  </p>
                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={() => setConfirmCancel(false)}
                      className="btn-ghost"
                      data-testid="cancel-abort-btn"
                    >
                      Non, garder
                    </button>
                    <button
                      onClick={async () => {
                        setCancelling(true);
                        try {
                          const res = await api.post(`/orders/cancel/${sessionId}`);
                          setCancelMessage(res.data.message);
                          setOrder(res.data.order);
                        } catch (e) {
                          setCancelMessage(
                            e.response?.data?.detail || "Erreur — merci de nous contacter par email."
                          );
                        } finally {
                          setCancelling(false);
                          setConfirmCancel(false);
                        }
                      }}
                      disabled={cancelling}
                      className="btn-fluo"
                      data-testid="cancel-confirm-btn"
                    >
                      <Ban size={14} /> {cancelling ? "..." : "Oui, annuler"}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmCancel(true)}
                  className="text-white/40 hover:text-magenta text-xs underline underline-offset-4"
                  data-testid="cancel-order-btn"
                >
                  Erreur dans ta commande ? Annuler maintenant
                </button>
              )}
            </div>
          )}

          {order?.status === "cancelled" && (
            <div className="fluo-card p-4 max-w-md mx-auto mb-8 border-red-500/40 text-red-400 text-sm">
              Cette commande est annulée.
            </div>
          )}

          <Link to="/" className="btn-fluo" data-testid="back-home-btn">
            <ShoppingBag size={18} /> Retour à la boutique
          </Link>
        </div>
      )}

      {(status === "failed" || status === "expired") && (
        <div data-testid="status-failed">
          <XCircle className="mx-auto mb-4 text-magenta" size={64} />
          <h1 className="font-display text-3xl mb-3">
            {status === "expired" ? "Session expirée" : "Paiement non validé"}
          </h1>
          <p className="text-white/60 mb-8">
            Aucun montant n&apos;a été prélevé. Tu peux réessayer.
          </p>
          <Link to="/cart" className="btn-fluo">
            Retour au panier
          </Link>
        </div>
      )}
    </div>
  );
}
